/*
 * Copyright (c) 2023 Pegasystems Inc.
 * All rights reserved.
 */

import type { CookieComplianceMethod, EnvironmentInfoObj } from '../../environment-info/types';
import type { FieldDefaultsObj } from '../../fields/types';
import type { ActionObject } from '../../globals';
import type { RoutingInfo } from '../../router/types';
import type { ServicesType } from '../../services/types';

import ServiceConfig from './service-config';
import type { ServiceConfigType } from './service-config';

export type ConfigurationType = {
  routingInfo: RoutingInfo;
  actionModel?: { [key: string]: any };
  additionalHeaders: { Authorization?: string | null; pzCTkn?: string };
  tokens: { [key: string]: any; C11NB2S: string };
  semanticUrl: string;
  queryParams: URLSearchParams;
  noPortal: any;
  timezone: string;
  noHistory: boolean;
  viewName: string;
  theme: any;
  fieldDefaults: FieldDefaultsObj;
  restServerConfig: ServicesType['server'];
  dynamicLoadComponents?: boolean;
  dynamicSemanticUrl?: boolean;
  enableRouting?: boolean;
  locale: string;
  environmentInfo: EnvironmentInfoObj;
  renderingMode?: EnvironmentInfoObj['renderingMode'];
  remoteCaseMapping?: { [key: string]: NonNullable<ActionObject['headers']> };
  envType: string;
  extras: { [key: string]: any };
  serviceConfig: ServiceConfigType;
  behaviorOverrides: { [key: string]: any };
  initBroadcast?: boolean;
  cookieComplianceMethod?: CookieComplianceMethod;
  appAlias: string | null;
  isExtraCallRequiredForDefaultHome: string;
  storeC11nPercentageAsDecimal?: boolean;
};

class Configuration {
  routingInfo: ConfigurationType['routingInfo'];
  actionModel: ConfigurationType['actionModel'];
  additionalHeaders: ConfigurationType['additionalHeaders'];
  tokens: ConfigurationType['tokens'];
  semanticUrl: ConfigurationType['semanticUrl'];
  queryParams: ConfigurationType['queryParams'];
  noPortal: ConfigurationType['noPortal'];
  timezone: ConfigurationType['timezone'];
  noHistory: ConfigurationType['noHistory'];
  viewName: ConfigurationType['viewName'];
  fieldDefaults: ConfigurationType['fieldDefaults'];
  theme: ConfigurationType['theme'];
  restServerConfig: ConfigurationType['restServerConfig'];
  dynamicLoadComponents: boolean;
  dynamicSemanticUrl: boolean;
  enableRouting: boolean;
  locale: ConfigurationType['locale'];
  environmentInfo: ConfigurationType['environmentInfo'];
  renderingMode: EnvironmentInfoObj['renderingMode'];
  remoteCaseMapping: NonNullable<ConfigurationType['remoteCaseMapping']>;
  envType: ConfigurationType['envType'];
  serviceConfig: ServiceConfig;
  private behaviorOverrides: ConfigurationType['behaviorOverrides'];
  initBroadcast: boolean;
  storePercentageAsDecimal: boolean;
  cookieComplianceMethod: CookieComplianceMethod;
  isExtraCallRequiredForDefaultHome: ConfigurationType['isExtraCallRequiredForDefaultHome'];
  constructor(config: ConfigurationType) {
    this.routingInfo = config.routingInfo;
    this.actionModel = config.actionModel;
    this.additionalHeaders = config.additionalHeaders;
    this.tokens = config.tokens;
    this.semanticUrl = config.semanticUrl;
    this.queryParams = config.queryParams;
    this.noPortal = config.noPortal;
    this.timezone = config.timezone;
    this.noHistory = config.noHistory;
    this.viewName = config.viewName;
    this.theme = config.theme;
    this.fieldDefaults = config.fieldDefaults;
    this.restServerConfig = config.restServerConfig;
    this.dynamicLoadComponents = config.dynamicLoadComponents !== undefined ? config.dynamicLoadComponents : true;
    this.dynamicSemanticUrl = config.dynamicSemanticUrl !== undefined ? config.dynamicSemanticUrl : true;
    this.enableRouting = config.enableRouting !== undefined ? config.enableRouting : true;
    this.locale = config.locale;
    this.environmentInfo = config.environmentInfo;
    this.renderingMode = config.renderingMode || 'FULL_PORTAL';
    this.remoteCaseMapping = config.remoteCaseMapping || {};
    this.envType = config.envType;
    this.serviceConfig = new ServiceConfig(config.serviceConfig);
    this.behaviorOverrides = config.behaviorOverrides;
    this.initBroadcast = !!config.initBroadcast;
    this.cookieComplianceMethod = config.cookieComplianceMethod ?? 'none';
    this.isExtraCallRequiredForDefaultHome = config.isExtraCallRequiredForDefaultHome ?? 'false';
    this.storePercentageAsDecimal = config.storeC11nPercentageAsDecimal ?? false;
  }
}

export default Configuration;
